/**
 * usePrivateStore — private org cache; phanHe không partialize.
 * Header/API ưu tiên getPhanHeFromStorage() khi cần PH đang mở.
 */
import { IRoleGroupViewModel } from '@/types/roleGroup';
import { RequestHeader } from '@/types/base';
import CommonJs from '@/utils/commonJs';
import CryptoService from '@/utils/cryptoService';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from './useAuthStore';
import { getPhanHeFromStorage } from '@/utils/phanHeStorage';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface PrivateState {
    phanHe: string;
    profile: any;
    company: {
        employees: {
            BPID: string;
            CVID: string;
            ID: string;
            SyncID: string;
            TenNhanVien: string;
        }[];
        department: {
            ID: string;
            ParentID: string;
            Prefix: string;
            Ten: string;

        }[];
    };
    roleGroup: IRoleGroupViewModel;
    treeSelectDepartment: {
        selectedValue: { value: string; label: string } | undefined;
        expandedKeys: string[];
        // treeData: any[];
        tenantID: string;
        idSelect: string | null;
        isEmployee: boolean;
        parentID: string | null;
        isCompany: boolean;
    };
    appHeader: {
        selectedDepartment: { value: string; label: string } | undefined;
        departmentId: string;
        departmentLabel: string;
        tenantID: string;
        kiemNhiemBpid: string;
    };
    // Global variables for planning
    departmentId: string | null;
    giaiDoan: {
        id: string | null,
        year: number | null,
        dateChoice: string | null,
        namTaiChinh: boolean | false,
        startDate: string | null,
        endDate: string | null
    },
    reporting: {
        giaiDoanID: string | null,
        dateChoice: string | null,
    },
    coCauHistory: {
        enabled: boolean;
        idCoCauGiaiDoan: string | null;
        changeId: string | null;
        snapshotAt: string | null;
        nam: number | null;
        label: string | null;
        tuThang: number | null;
        denThang: number | null;
        selectedDepartmentId: string | null;
        selectedDepartmentName: string | null;
        selectedDepartmentParentId: string | null;
        isEmployee: boolean;
        isCompany: boolean;
    },
    dateChoiceDefault: string | null,
    /** Quy trình root đang lọc — dùng chung các màn Quy trình (persist `private-store`). */
    quyTrinhID: string | null,
    setQuyTrinhID: (id: string | null | undefined) => void;
    getQuyTrinhID: () => string | undefined;
    setDateChoiceDefault: (dateChoice: string | null) => void;
    getDateChoiceDefault: () => string | null;
    setReporting: (id: string | null, dateChoice: string | null) => void;
    setCoCauHistory: (data: Partial<PrivateState['coCauHistory']>) => void;
    clearCoCauHistory: () => void;
    setPhanHe: (phanHe: any) => void;
    getPhanHe: () => string;

    setCompany: (company: any) => void;
    getProfile: () => any;
    getDepartments: () => any;
    getEmployees: () => any;
    setRoleGroup: (roleGroup: any) => void;
    getRoleGroup: () => any;

    // Tree Select Department methods
    setTreeSelectDepartment: (data: {
        selectedValue?: { value: string; label: string } | undefined;
        expandedKeys?: string[];
        tenantID?: string;
        idSelect?: string | null;
        isEmployee?: boolean;
        parentID?: string | null;
        isCompany?: boolean;
    },
    isActionPlan?: boolean
    ) => void;
    getTreeSelectDepartment: () => {
        selectedValue: { value: string; label: string } | undefined;
        expandedKeys: string[];
        tenantID: string;
        //
        idSelect: string | null;
        isEmployee: boolean;
        parentID: string | null;
        isCompany: boolean;

    };
    clearTreeSelectDepartment: () => void;

    // App Header methods
    setAppHeader: (data: {
        selectedDepartment?: { value: string; label: string } | undefined;
        departmentId?: string;
        departmentLabel?: string;
        tenantID?: string;
        kiemNhiemBpid?: string;
    }) => void;
    getAppHeader: () => {
        selectedDepartment: { value: string; label: string } | undefined;
        departmentId: string;
        departmentLabel: string;
        tenantID: string;
        kiemNhiemBpid: string;
    };
    clearAppHeader: () => void;

    // Global variables setters
    setDepartmentId: (departmentId: string | null) => void;
    setGiaiDoan: (id: string | null, year: number | null, datechoice: string | null, namTaiChinh: boolean | false, startDate: string | null, endDate: string | null) => void;

    clearStore: () => void;
    initRequestHeader: () => void;
    // Request header methods
    getRequestHeader: () => RequestHeader;
    /** Luôn build từ state hiện tại (không đọc sessionStorage cache). */
    getLiveRequestHeader: (overrides?: {
        idSelected?: string | null;
        year?: number | null;
        dateChoice?: string | null;
    }) => RequestHeader;
}

export const usePrivateStore = create<PrivateState>()(
    persist(
        (set, get) => ({
            phanHe: '',
            profile: '',
            company: {
                employees: [],
                department: [],
            },
            roleGroup: {
                id: '',
                ten: '',
                ghiChu: '',
                fullControl: false,
                quanLy: false,
                roles: [],
                selectedModules: {},
                siteModule: 0,
                site: '',
            },
            treeSelectDepartment: {
                selectedValue: undefined,
                expandedKeys: [],
                tenantID: '',
                idSelect: null,
                isEmployee: false,
                parentID: null,
                isCompany: false,
            },
            appHeader: {
                selectedDepartment: undefined,
                departmentId: '',
                departmentLabel: '',
                tenantID: '',
                kiemNhiemBpid: '',
            },
            // Global variables initialization
            departmentId: null,
            giaiDoan: {
                id: null,
                year: new Date().getFullYear(),
                dateChoice: null,
                namTaiChinh: false,
                startDate: null,
                endDate: null
            },
            reporting: {
                giaiDoanID: null,
                dateChoice: null,
            },
            coCauHistory: {
                enabled: false,
                idCoCauGiaiDoan: null,
                changeId: null,
                snapshotAt: null,
                nam: null,
                label: null,
                tuThang: null,
                denThang: null,
                selectedDepartmentId: null,
                selectedDepartmentName: null,
                selectedDepartmentParentId: null,
                isEmployee: false,
                isCompany: false,
            },
            dateChoiceDefault: null,
            quyTrinhID: null,
            setQuyTrinhID: (id: string | null | undefined) => {
                const t = String(id ?? '').trim();
                set({ quyTrinhID: t || null });
            },
            getQuyTrinhID: () => {
                const t = String(get().quyTrinhID ?? '').trim();
                if (t) return t;
                if (typeof window === 'undefined') return undefined;
                try {
                    const legacy = window.sessionStorage.getItem('getQuyTrinhID');
                    const lt = String(legacy ?? '').trim();
                    if (lt) {
                        set({ quyTrinhID: lt });
                        window.sessionStorage.removeItem('getQuyTrinhID');
                        return lt;
                    }
                } catch {
                    // ignore
                }
                return undefined;
            },
            setDateChoiceDefault: (dateChoice: string | null) => {
                set({ dateChoiceDefault: dateChoice });
                get().initRequestHeader();
            },
            getDateChoiceDefault: () => get().dateChoiceDefault,
            //idGiaiDoan: null,
            //selectedYear: new Date().getFullYear(),
            setRoleGroup: (roleGroup: IRoleGroupViewModel) => {
                set({ roleGroup });
            },
            getRoleGroup: () => get().roleGroup,
            setPhanHe: (phanHe) => {
                set({ phanHe });
                get().initRequestHeader();
            },
            getPhanHe: () => get().phanHe,

            setCompany: (company: any) => {
                const jsonData = {
                    profile: CryptoService.encryptAES(JSON.stringify(company.profile)),
                    departments: CryptoService.encryptAES(JSON.stringify(company.departments)),
                    employees: CryptoService.encryptAES(JSON.stringify(company.employees)),
                };
                set({
                    profile: jsonData.profile,
                    company: {
                        employees: jsonData.employees as any,
                        department: jsonData.departments as any,
                    },
                });
            },
            getProfile: () => {
                try {
                    const profile = get().profile;
                    if (CommonJs.isEmpty(profile)) return [];
                    const decryptData = CryptoService.decryptAES(profile);
                    const jsonData = JSON.parse(decryptData);
                    return jsonData;
                } catch (error) {
                    console.error(error);
                    return [];
                }
            },
            getDepartments: () => {
                try {
                    const company = get().company;
                    if (CommonJs.isEmpty(company)) return [];
                    const decryptData = CryptoService.decryptAES(company.department as any);
                    const jsonData = JSON.parse(decryptData);
                    return jsonData;
                } catch (error) {
                    console.error(error);
                    return [];
                }
            },
            getEmployees: () => {
                try {
                    const company = get().company;
                    if (CommonJs.isEmpty(company)) return [];
                    const decryptData = CryptoService.decryptAES(company.employees as any);
                    const jsonData = JSON.parse(decryptData);
                    return jsonData;
                } catch (error) {
                    console.error(error);
                    return [];
                }
            },

            setTreeSelectDepartment: (data , isActionPlan = false) => {
                const currentState = get().treeSelectDepartment;
                set({
                    treeSelectDepartment: {
                        selectedValue: data.selectedValue !== undefined ? data.selectedValue : currentState.selectedValue,
                        expandedKeys: data.expandedKeys !== undefined ? data.expandedKeys : currentState.expandedKeys,
                        tenantID: data.tenantID !== undefined ? data.tenantID : currentState.tenantID,
                        idSelect: data.idSelect !== undefined ? data.idSelect : currentState.idSelect,
                        isEmployee: data.isEmployee !== undefined ? data.isEmployee : currentState.isEmployee,
                        parentID: data.parentID !== undefined ? data.parentID : currentState.parentID,
                        isCompany: data.isCompany !== undefined ? data.isCompany : currentState.isCompany,
                    },
                    departmentId: data.idSelect !== undefined ? data.idSelect : currentState.idSelect
                });

                if (!isActionPlan) {
                    get().initRequestHeader();
                }
            },
         
            getTreeSelectDepartment: () => get().treeSelectDepartment,
            clearTreeSelectDepartment: () => {
                set({
                    treeSelectDepartment: {
                        selectedValue: undefined,
                        expandedKeys: [],
                        tenantID: '',
                        idSelect: null,
                        isEmployee: false,
                        parentID: null,
                        isCompany: false,
                    },
                });
                get().initRequestHeader();
            },

            setAppHeader: (data) => {
                const currentState = get().appHeader;
                set({
                    appHeader: {
                        selectedDepartment:
                            data.selectedDepartment !== undefined ? data.selectedDepartment : currentState.selectedDepartment,
                        departmentId: data.departmentId !== undefined ? data.departmentId : currentState.departmentId,
                        departmentLabel: data.departmentLabel !== undefined ? data.departmentLabel : currentState.departmentLabel,
                        tenantID: data.tenantID !== undefined ? data.tenantID : currentState.tenantID,
                        kiemNhiemBpid: data.kiemNhiemBpid !== undefined ? data.kiemNhiemBpid : currentState.kiemNhiemBpid,
                    },
                });
            },
            getAppHeader: () => get().appHeader,
            clearAppHeader: () => {
                set({
                    appHeader: {
                        selectedDepartment: undefined,
                        departmentId: '',
                        departmentLabel: '',
                        tenantID: '',
                        kiemNhiemBpid: '',
                    },
                });
            },

            // Global variables setters
            setDepartmentId: (departmentId: string | null) => set({ departmentId }),

            setGiaiDoan: (id: string | null, year: number | null, dateChoice: string | null, namTaiChinh: boolean | false, startDate: string | null, endDate: string | null) => {
                set((state) => {
                    if (id == null) {
                        id = state.giaiDoan.id;
                    }
                    if (year == null) {
                        year = state.giaiDoan.year ?? new Date().getFullYear();
                    }
                    if (namTaiChinh == null) {
                        namTaiChinh = state.giaiDoan.namTaiChinh;
                    }
                    if (startDate == null) {
                        startDate = state.giaiDoan.startDate;
                    }
                    if (endDate == null) {
                        endDate = state.giaiDoan.endDate;
                    }
                    if (dateChoice == null) {
                        dateChoice = state.giaiDoan.dateChoice;
                    }

                    // Auto-update dateChoice when year changes
                    if (startDate && endDate && year) {
                        // Extract year from current dateChoice
                        let currentYear: number | null = null;
                        if (dateChoice) {
                            const match = dateChoice.match(/\/(\d{4})/);
                            if (match) {
                                currentYear = parseInt(match[1]);
                            }
                        }
                        
                        // Only update if year is different
                        if (currentYear !== null && currentYear !== year) {
                            // Get day and month from startDate, combine with selected year
                            const originalStart = new Date(startDate);
                            const startDay = originalStart.getDate();
                            const startMonth = originalStart.getMonth(); // 0-11
                            
                            // Create new start date with selected year
                            const start = new Date(year, startMonth, startDay);
                            
                            // Calculate end date: 12 months later, last day of the month before anniversary
                            const end = new Date(year, startMonth + 12, 0); // Day 0 = last day of previous month
                            
                            const formatDate = (date: Date) => {
                                const day = String(date.getDate()).padStart(2, '0');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const year = date.getFullYear();
                                return `${day}/${month}/${year}`;
                            };
                            
                            dateChoice = `${formatDate(start)} - ${formatDate(end)}`;
                        }
                    }

                    return {
                        giaiDoan: {
                            id: id,
                            year: year,
                            dateChoice: dateChoice,
                            namTaiChinh: namTaiChinh,
                            startDate: startDate,
                            endDate: endDate
                        }
                    };
                });
                get().initRequestHeader();
            },

            setReporting: (id: string | null, dateChoice: string | null) =>
                set(() => ({
                    reporting: { giaiDoanID: id, dateChoice: dateChoice }
                })),
            setCoCauHistory: (data) =>
                set((state) => ({
                    coCauHistory: {
                        ...state.coCauHistory,
                        ...data,
                        enabled: data.enabled ?? state.coCauHistory.enabled,
                    },
                })),
            clearCoCauHistory: () =>
                set({
                    coCauHistory: {
                        enabled: false,
                        idCoCauGiaiDoan: null,
                        changeId: null,
                        snapshotAt: null,
                        nam: null,
                        label: null,
                        tuThang: null,
                        denThang: null,
                        selectedDepartmentId: null,
                        selectedDepartmentName: null,
                        selectedDepartmentParentId: null,
                        isEmployee: false,
                        isCompany: false,
                    },
                }),

            clearStore: () => {
                // Clear localStorage for private-store
                set({
                    phanHe: '',
                    profile: '',
                    company: {
                        employees: [] as any,
                        department: [] as any,
                    },

                    treeSelectDepartment: {
                        selectedValue: undefined,
                        expandedKeys: [],
                        // treeData: [],
                        tenantID: '',
                        idSelect: null,
                        isEmployee: false,
                        parentID: null,
                        isCompany: false,
                    },
                    appHeader: {
                        selectedDepartment: undefined,
                        departmentId: '',
                        departmentLabel: '',
                        tenantID: '',
                        kiemNhiemBpid: '',
                    },
                    // Reset global variables
                    departmentId: null,
                    giaiDoan: {
                        id: null,
                        year: new Date().getFullYear(),
                        dateChoice: null,
                        namTaiChinh: false,
                        startDate: null,
                        endDate: null
                    },
                    reporting: {
                        giaiDoanID: null,
                        dateChoice: null,
                    },
                    coCauHistory: {
                        enabled: false,
                        idCoCauGiaiDoan: null,
                        changeId: null,
                        snapshotAt: null,
                        nam: null,
                        label: null,
                        tuThang: null,
                        denThang: null,
                        selectedDepartmentId: null,
                        selectedDepartmentName: null,
                        selectedDepartmentParentId: null,
                        isEmployee: false,
                        isCompany: false,
                    },
                    dateChoiceDefault: null,
                    quyTrinhID: null,
                });
            },

            initRequestHeader: () => {
                const state = get();
                const tenantID = useAuthStore.getState().tenantID;
                const currentYear = new Date().getFullYear();
                const phanHe = getPhanHeFromStorage() || state.phanHe || 'bsc';
                const data = {
                    tenantID: tenantID,
                    phanHe: phanHe,
                    selectPeriod: {
                        phanHe: phanHe,
                        idGiaiDoan: state.giaiDoan.id ?? '',
                        year: state.giaiDoan.year ?? currentYear,
                        dateChoice: state.giaiDoan.dateChoice ?? '',
                    },
                    selectTree: {
                        idSelected: state.treeSelectDepartment.idSelect ?? '',
                        isEmployee: state.treeSelectDepartment.isEmployee,
                        idParentSelected: state.treeSelectDepartment.parentID ?? '',
                        isCompany: state.treeSelectDepartment.isCompany,
                    },
                };
                if (typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined') {
                    window.sessionStorage.setItem('getRequestHeader', JSON.stringify(data));
                }
            },
            // Request header methods
            getRequestHeader: () => {
                const state = get();
                const tenantID = useAuthStore.getState().tenantID;
                const currentYear = new Date().getFullYear();
                const phanHe = getPhanHeFromStorage() || state.phanHe || 'bsc'; // Handle null, undefined, empty string

                const isBrowser = typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
                const currentData = isBrowser ? window.sessionStorage.getItem('getRequestHeader') : undefined;

                if (currentData) {
                    const storedData = JSON.parse(currentData) as RequestHeader;

                    if (storedData.tenantID == tenantID) {
                        return storedData;
                    }
                }

                const data = {
                    tenantID: tenantID || '',
                    phanHe: phanHe,
                    selectPeriod: {
                        phanHe: phanHe,
                        idGiaiDoan: state.giaiDoan.id ?? '',
                        year: state.giaiDoan.year ?? currentYear,
                        dateChoice: state.giaiDoan.dateChoice ?? '',
                    },
                    selectTree: {
                        idSelected: state.treeSelectDepartment.idSelect ?? '',
                        isEmployee: state.treeSelectDepartment.isEmployee,
                        idParentSelected: state.treeSelectDepartment.parentID ?? '',
                        isCompany: state.treeSelectDepartment.isCompany ,
                    },
                };
                if (isBrowser) {
                    window.sessionStorage.setItem('getRequestHeader', JSON.stringify(data));
                }

                return data;
            },
            getLiveRequestHeader: (overrides) => {
                const state = get();
                const tenantID = useAuthStore.getState().tenantID;
                const currentYear = new Date().getFullYear();
                const phanHe = getPhanHeFromStorage() || state.phanHe || 'bsc';
                const idSelected = String(
                    overrides?.idSelected ?? state.treeSelectDepartment.idSelect ?? ''
                ).trim();
                const year = overrides?.year ?? state.giaiDoan.year ?? currentYear;
                const dateChoice = overrides?.dateChoice ?? state.giaiDoan.dateChoice ?? '';
                return {
                    tenantID: tenantID || '',
                    phanHe,
                    selectPeriod: {
                        phanHe,
                        idGiaiDoan: state.giaiDoan.id ?? '',
                        year,
                        dateChoice,
                    },
                    selectTree: {
                        idSelected,
                        isEmployee: state.treeSelectDepartment.isEmployee,
                        idParentSelected: state.treeSelectDepartment.parentID ?? '',
                        isCompany: state.treeSelectDepartment.isCompany,
                    },
                };
            },
        }),
        {
            name: 'private-store', // Key trong localStorage
            partialize: (state) => ({
                // không persist phanHe — lấy từ sessionStorage
                profile: state.profile,
                company: state.company,
                roleGroup: state.roleGroup,
                treeSelectDepartment: state.treeSelectDepartment,
                appHeader: state.appHeader,
                // Persist global variables
                departmentId: state.departmentId,
                giaiDoan: state.giaiDoan,
                reporting: state.reporting,
                coCauHistory: state.coCauHistory,
                dateChoiceDefault: state.dateChoiceDefault,
                quyTrinhID: state.quyTrinhID,
            }),
        }
    )
);
